import { provideHttpClient, withInterceptors, withXhr } from "@angular/common/http";
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from "@angular/core";
import { provideRouter, withPreloading } from "@angular/router";
import { provideServiceWorker } from "@angular/service-worker";
import { provideMissingTranslationHandler, provideTranslateService, TranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { pendingRequestsInterceptor$ as pendingRequestsInterceptor } from "ng-http-loader";
import { ModalModule } from "ngx-bootstrap/modal";
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { environment } from "src/environments/environment";
import { provideApiBaseUrl } from "./api/provide-api-base-url";
import { provideNSwagClient } from "./api/provide-n-swag-client";
import { APP_ROUTES } from "./app.routes";
import { provideAppUserSession } from "./core/authentication/session.service";
import { AppMissingTranslationHandler } from "./core/class/app-missing-translation-handler";
import { provideAppRouteReuseStrategy } from "./core/class/app-route-reuse-strategy";
import { provideAppTitleStrategy } from "./core/class/app-title-strategy";
import { AppTranslateService } from "./core/class/app-translate-service";
import { PreloadUnlessSpecified } from "./core/class/preload-unless-specified";
import { AppSettings } from "./core/config/app-settings.config";
import { provideSentryTraceService } from "./core/config/sentry.config";
import { TRANSLATE_LOADER_PREFIX, TRANSLATE_LOADER_SUFFIX } from "./core/constants/translate";
import { provideAppErrorHandler } from "./core/error/app-error-handler";
import { searchAndSaveCampaignCodeOnAppInit } from "./core/functions/search-and-save-campaign-code";
import { setRobotsMetaTagValueOnAppInit } from "./core/functions/set-robots-meta-tag-value-on-app-init";
import { appHttpInterceptor } from "./core/interceptors/app-http.interceptor";
import { provideAppDefaultTranslation } from "./core/providers/provide-default-translation";
import { PublicMaintenanceMessageService } from "./core/services/public-maintenance-message.service";
import { ThemeService } from "./core/services/theme.service";

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(APP_ROUTES, withPreloading(PreloadUnlessSpecified)),
    provideHttpClient(withXhr(), withInterceptors([pendingRequestsInterceptor, appHttpInterceptor])),
    provideServiceWorker("ngsw-worker.js", {
      enabled: AppSettings.enableServiceWorker,
      registrationStrategy: "registerWhenStable:30000",
    }),
    provideTranslateService({
      fallbackLang: undefined, // missingTranslationHandler will handle missing translations
      missingTranslationHandler: provideMissingTranslationHandler(AppMissingTranslationHandler),
      loader: provideTranslateHttpLoader({ prefix: TRANSLATE_LOADER_PREFIX, suffix: TRANSLATE_LOADER_SUFFIX }),
    }),
    importProvidersFrom(ModalModule, TooltipModule),
    { provide: TranslateService, useClass: AppTranslateService },
    {
      provide: LOCALE_ID,
      useFactory: (): string => inject(TranslateService, { optional: true })?.getCurrentLang() ?? AppSettings.defaultLanguage,
    },
    provideNSwagClient(),
    // Provide api url to NSwag's Client class
    provideApiBaseUrl(environment.apiUrl),
    // Have the default translation ready before the app renders it's view
    provideAppDefaultTranslation({ pickUserDefault: true }),
    PublicMaintenanceMessageService.provideMaintenanceMessage(),
    ThemeService.provideThemes(),
    provideAppUserSession(),
    // Provide our own Custom Class, that also extends Sentry's Error Handler
    provideAppErrorHandler(),
    // To translate the page's titles on the user's selected / detected language
    provideAppTitleStrategy(),
    provideAppRouteReuseStrategy(),
    provideSentryTraceService(),
    // Stores campaign code from the url to be used later
    searchAndSaveCampaignCodeOnAppInit(),
    // Sets the initial value for the Robots Meta Tag, it can change on route navigation
    setRobotsMetaTagValueOnAppInit(),
  ],
};
